import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api";
import { REFETCH_INTERVAL_MS } from "../lib/constants";
import type { StatusResponse } from "../types/api";

/**
 * Fetch GET /api/status with 30s auto-refetch.
 *
 * Polling cadence rationale (see 03_api_contract.md §7):
 *   - Server refreshes its cache every 60s (`pollIntervalMs`).
 *   - FE polls every 30s — half the server period — so the UI lands the latest
 *     server cache within at most ~30s of staleness.
 *
 * `refetchIntervalInBackground: false` pauses polling when the tab is hidden;
 * `refetchOnWindowFocus` (configured globally) will refresh on return.
 *
 * No unwrap — the contract returns `StatusResponse` at the root.
 *
 * Errors propagate to the query's error state. Components keep the last
 * successful `data` snapshot via TanStack Query's `keepPreviousData`-like
 * behavior (we additionally read `previousData` via `placeholderData`).
 */
export function useStatus() {
  return useQuery<StatusResponse>({
    queryKey: ["status"],
    queryFn: ({ signal }) =>
      apiGet<StatusResponse>("/api/status", signal ? { signal } : undefined),
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });
}
